const Preconditions = require('./Preconditions.js');
const E164 = require('./E164.js');

export function notBlank(value) {
  if (value == null) return false;
  value = value.trim();
  if (value.length == 0) return false;

  return true;
}

export function isBlank(value) {
  return !notBlank(value);
}

export function isUSPhoneNumber(number) {
  return E164.isUSPhoneNumber(number);
}

export function isValidUrl(url) {
  if (!url) return false;
  return url.includes('https://') || url.includes('http://');
}

export function isAllDigits(value) {
  for (let i = 0; i < value.length; i++) {
    let cur_char = value.charAt(i);

    if (
      cur_char === '0' ||
      cur_char === '1' ||
      cur_char === '2' ||
      cur_char === '3' ||
      cur_char === '4' ||
      cur_char === '5' ||
      cur_char === '6' ||
      cur_char === '7' ||
      cur_char === '8' ||
      cur_char === '9'
    ) {
      continue;
    }

    return false;
  }

  return true;
}

export function isBlankOrUSPhoneNumber(number) {
  if (number == null) return true;
  if (number.trim().length == 0) return true;

  return E164.isUSPhoneNumber(number);
}

export function isBlankOrEmailAddress(email) {
  if (email == null) return true;
  if (email.trim().length == 0) return true;

  return isEmailAddress(email);
}

export function isBlankOrEmailAddresses(email) {
  if (email == null) return true;
  if (email.trim().length == 0) return true;

  let addresses = email.split(';');

  for (let i = 0; i < addresses.length; i++) {
    if (!isBlankOrEmailAddress(addresses[i])) return false;
  }

  return true;
}

export function isEmailAddresses(email) {
  if (email == null) return false;
  if (email.trim().length == 0) return false;

  let addresses = email.split(';');

  let valid_email_count = 0;

  for (let i = 0; i < addresses.length; i++) {
    if (!isBlankOrEmailAddress(addresses[i])) return false;
    if (isEmailAddress(addresses[i])) valid_email_count++;
  }

  return valid_email_count > 0;
}

export function isEmailAddress(email) {
  if (!Preconditions.isString(email)) return false;

  if (email.includes('<')) return false;

  if (email.includes('>')) return false;

  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

export function isBlankOrValidUSDateWithOptionalYear(date) {
  if (date == null) return true;
  if (date.trim().length == 0) return true;

  return isValidUSDateWithOptionalYear(date);
}

export function isValidUSDateWithOptionalYear(date, enforce_year) {
  if (!Preconditions.isString(date)) return false;

  let parts = date.split('/');

  if (parts.length != 2 && parts.length != 3) return false;

  let day = parseInt(parts[1]);
  if (isNaN(day)) return false;
  if (day < 1 || day > 31) return false;

  let month = parseInt(parts[0]);
  if (isNaN(month)) return false;
  if (month < 1 || month > 12) return false;

  if (day > getDaysInMonth(month)) return false;

  if (parts.length == 2 && !enforce_year) return true;

  let year = parseInt(parts[2]);
  if (isNaN(year)) return false;
  if (year < 100) return false;
  if (year > 9999) return false;

  return true;
}

export function isOverAgeInYears(min_age_in_years, birthdate_str, current_date_str) {
  if (
    !Preconditions.isString(birthdate_str) ||
    !isValidUSDateWithOptionalYear(birthdate_str, true) ||
    !current_date_str
  ) {
    return false;
  }

  let today = new Date(current_date_str);
  let birthdate = new Date(birthdate_str);
  let age = today.getFullYear() - birthdate.getFullYear();
  let delta = today.getMonth() - birthdate.getMonth();

  if (delta < 0 || (delta === 0 && today.getDate() < birthdate.getDate())) {
    age--;
  }

  // prevents weird bug where entering a nonsense number can pass the check
  return age >= min_age_in_years && age < 120;
}

export function getDaysInMonth(month_num) {
  if (month_num === 1) return 31;
  if (month_num === 2) return 29;
  if (month_num === 3) return 31;
  if (month_num === 4) return 30;
  if (month_num === 5) return 31;
  if (month_num === 6) return 30;
  if (month_num === 7) return 31;
  if (month_num === 8) return 31;
  if (month_num === 9) return 30;
  if (month_num === 10) return 31;
  if (month_num === 11) return 30;
  if (month_num === 12) return 31;

  return NaN;
}

export function isValidISODateFormat(date) {
  // Check if the date is in YYYY-MM-DD format
  const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

  if (!regex.test(date)) {
    return false;
  }

  try {
    // Parse the date
    const parts = date.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    // Check if year is reasonable
    if (year < 1800 || year > 2100) {
      return false;
    }

    // Create a Date object to validate the date
    const date_obj = new Date(year, month - 1, day);
    return (
      date_obj.getFullYear() === year &&
      date_obj.getMonth() === month - 1 &&
      date_obj.getDate() === day
    );
  } catch (e) {
    return false;
  }
}

export function isBlankOrValidISODateFormat(date) {
  if (date == null) return true;
  if (date.trim().length == 0) return true;

  return isValidISODateFormat(date);
}

export function isValidDateFormat(date) {
  // Check if the date is in YYYY-MM-DD format
  if (isValidISODateFormat(date)) return true;

  // Check if the date is in MM/DD/YYYY format
  return isValidUSDateWithOptionalYear(date, true);
}

export function isValidTaxId(tax_id) {
  if (tax_id == null) return true;
  if (tax_id.trim().length == 0) return true;

  let test_value = tax_id ?? '';
  test_value = test_value.trim();

  if (test_value.length === 0) return true;

  let numb = test_value.match(/\d/g);
  numb = numb ? numb.join('') : '';

  return numb.length === 9;
}

/**
 * Validates if the given email is an actual email address.
 * Also validates that the email is not a group email (e.g., admin@, support@) or a personal email (e.g., gmail.com, yahoo.com).
 * @param {string} email - The email address to validate.
 * @returns {boolean} True if the email is an official email address, false otherwise.
 */
export function isValidOfficialEmail(email) {
  if (!email) return false;
  if (!isEmailAddress(email)) return false;

  const group_email_prefixes = [
    'admin@',
    'support@',
    'help@',
    'info@',
    'contact@',
    'sales@',
    'hello@',
    'feedback@',
    'inquiries@',
    'questions@',
    'assistance@',
    'team@',
    'careers@',
    'jobs@',
    'marketing@',
    'legal@',
    'compliance@',
  ];

  const personal_email_domains = [
    'gmail.com',
    'outlook.com',
    'hotmail.com',
    'aol.com',
    'yahoo.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'mail.com',
    'zoho.com',
    'protonmail.com',
  ];

  const lowercase_email = email.toLowerCase();

  for (let prefix of group_email_prefixes) {
    if (lowercase_email.startsWith(prefix)) {
      return false;
    }
  }

  const domain = lowercase_email.split('@')[1];
  if (domain && personal_email_domains.includes(domain)) {
    return false;
  }

  return true;
}

export default {
  isEmailAddress,
  isValidUSDateWithOptionalYear,
  isValidISODateFormat,
  isBlankOrValidISODateFormat,
  isValidDateFormat,
  getDaysInMonth,
  isUSPhoneNumber,
  isValidUrl,
  isBlankOrValidUSDateWithOptionalYear,
  isBlankOrUSPhoneNumber,
  isBlankOrEmailAddress,
  isAllDigits,
  notBlank,
  isBlank,
  isValidTaxId,
  isValidOfficialEmail,
  isOverAgeInYears,
  isEmailAddresses,
  isBlankOrEmailAddresses
};
